import { useState } from 'react';
import { Banner, Grid, Link, PageLoader, Panel, PanelSection, Paragraph } from '@customerio/pluma-components/react';
import { type Plan, Plans, type PlanType } from '@/services/billing/plans';
import { ContractsSalesDrawer } from '../contracts-sales/components/contracts-sales-drawer';
import { PlanInfo } from './plan-info';
import { StartupPlanInfo } from './startup-plan-info';
import { useAvailablePlansAnalytics } from '../hooks/use-available-plans-analytics';
import { findOrdwayPlan } from '../utils/find-ordway-plan';
import { useAvailablePlansLoader, useAvailablePlansModel } from '../hooks/use-available-plans';

// The Journeys plan comparison always shows these three static plans, in this order. Ordway plan data
// (when loaded) is merged in by plan type to supply the checkout link's `ordwayPlanID`.
const JOURNEYS_PLANS: Plan[] = [Plans.Essentials, Plans.Premium, Plans.Enterprise];

/** The full external pricing page, linked beneath the grid. */
const FULL_PRICING_URL = 'https://customer.io/pricing/';

type AvailablePlansGridProps = {
  /** Set this on a page that gives the trial status itself, such as the paywall. */
  hideExpirationLabel?: boolean;
};

/**
 * The Journeys available-plans comparison grid: the three plan cards, an optional startup-program
 * panel, and a link to the full pricing page. While the Ordway plan catalog loads it shows a page
 * loader; on failure it shows an error banner with a retry.
 */
export function AvailablePlansGrid({ hideExpirationLabel }: AvailablePlansGridProps = {}) {
  const model = useAvailablePlansModel();
  const loader = useAvailablePlansLoader();
  const analytics = useAvailablePlansAnalytics();
  // The plan whose "Let's talk" button was clicked; opens the contracts-sales sidebar.
  const [letsTalkPlan, setLetsTalkPlan] = useState<PlanType | null>(null);

  if (loader.isLoading) {
    return (
      <Grid py="2000">
        <PageLoader label="Loading plans…" />
      </Grid>
    );
  }

  if (loader.isError) {
    return (
      <Banner
        variant="critical"
        description="Available plans couldn't be loaded."
        action={{ label: 'Try again', onClick: loader.retry }}
      />
    );
  }

  return (
    <>
      <Grid
        columns="repeat(3, 1fr)"
        gap="200"
        mx="auto"
        style={{ width: '80%' }}
        data-test-available-plans-grid
        data-test-available-plans-messaging
      >
        {JOURNEYS_PLANS.map((plan) => (
          <PlanInfo
            key={plan.name}
            plan={plan}
            ordwayPlan={findOrdwayPlan(model.ordwayPlans, plan)}
            model={model}
            onLetsTalk={setLetsTalkPlan}
            onCtaClick={analytics.trackCtaClicked}
            hideExpirationLabel={hideExpirationLabel}
          />
        ))}
        {model.showStartupPlans ? <StartupPlanInfo onTrackClick={analytics.trackStartupCardClicked} /> : null}
        <Panel style={{ gridColumn: 'span 3' }}>
          <PanelSection display="flex" justifyContent="center">
            <Paragraph>
              View our{' '}
              <Link
                isUnderlined={true}
                href={FULL_PRICING_URL}
                isExternal={true}
                onClick={analytics.trackViewAllFeatures}
              >
                full plan breakdown
              </Link>
              .
            </Paragraph>
          </PanelSection>
        </Panel>
      </Grid>
      <ContractsSalesDrawer
        isOpen={letsTalkPlan != null}
        planClicked={letsTalkPlan ?? undefined}
        onClose={() => setLetsTalkPlan(null)}
      />
    </>
  );
}
